// Pure helpers for keeping the Support desk's ticket list in step with the proxy
// WITHOUT re-downloading it.
//
// The list is ~170 KB for 210 tickets. The desk used to fetch all of it on every
// ticket write anywhere in the office and on a 2-minute timer: 27 MB in one
// session on the sidebar meter. Now it fetches it once, then asks the proxy only
// for tickets touched since the newest one it already holds.

/**
 * `updatedAt` is stamped by whichever browser made the write, with that PC's
 * clock. Asking for "after my newest" would miss a write from a PC whose clock
 * runs behind, so the cursor is pulled back by this margin. Re-sending the
 * handful of tickets touched in the margin is cheap; missing one is not.
 */
export const CLOCK_SKEW_MS = 2 * 60_000;

/** Newest updatedAt held, minus the skew margin; 0 means "send everything". */
export const deltaCursor = (rows = [], skewMs = CLOCK_SKEW_MS) => {
    let max = 0;
    for (const r of rows) { const u = Number(r?.updatedAt) || 0; if (u > max) max = u; }
    return max > 0 ? Math.max(1, max - skewMs) : 0;
};

const byNewest = (a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0);

/**
 * Merge changed rows into the held list (replace by id, add new), newest first.
 * Returns the SAME array when nothing actually changed, so a no-op delta does not
 * re-render every screen that reads the list.
 */
export const mergeTicketRows = (current = [], changed = []) => {
    if (!changed.length) return current;
    const byId = new Map(current.map(r => [r.id, r]));
    let touched = false;
    for (const r of changed) {
        if (!r?.id) continue;
        const prev = byId.get(r.id);
        if (prev && JSON.stringify(prev) === JSON.stringify(r)) continue;
        byId.set(r.id, r);
        touched = true;
    }
    if (!touched) return current;
    return [...byId.values()].sort(byNewest);
};

/**
 * After a delta, does the held list still match the server? The proxy always
 * reports the WHOLE tree's size; if ours differs, a ticket was deleted (or a
 * delta was missed) and the next fetch must be a full one.
 */
export const needsFullResync = (merged = [], serverCount) =>
    Number.isFinite(Number(serverCount)) && serverCount !== null && merged.length !== Number(serverCount);
